import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { EMPLOYEE_NAV } from "./_nav";
import { api, errFrom, absUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { toast } from "sonner";
import { Camera, Car, Check, X, ImagePlus } from "lucide-react";

function tryGeolocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  });
}

export default function EmployeeCustomers() {
  const [customer, setCustomer] = useState("");
  const [notes, setNotes] = useState("");
  const [type, setType] = useState("pre");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);

  async function loadRecent() {
    try {
      const { data } = await api.get("/employee/my-uploads");
      setRecent(data.customer_inspections.slice(0, 6));
    } catch { /* ignore */ }
  }
  useEffect(() => { loadRecent(); }, []);

  function addFiles(list) {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    const max = 10;
    const combined = [...files, ...arr.map((f) => ({ file: f, preview: URL.createObjectURL(f) }))].slice(0, max);
    if ([...files, ...arr].length > max) toast.message(`Max ${max} photos — extra ignored.`);
    setFiles(combined);
  }
  function removeFile(idx) { setFiles(files.filter((_, i) => i !== idx)); }

  async function submit() {
    if (!customer.trim()) return toast.error("Customer name is required");
    if (!files.length) return toast.error("Select or capture at least one image");
    setLoading(true);
    try {
      const geo = await tryGeolocation();
      const fd = new FormData();
      fd.append("customer_name", customer);
      fd.append("inspection_type", type);
      fd.append("notes", notes || "");
      if (geo) { fd.append("latitude", String(geo.latitude)); fd.append("longitude", String(geo.longitude)); }
      files.forEach((f) => fd.append("files", f.file));
      await api.post("/employee/customer-inspection", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${files.length} ${type === "pre" ? "pre" : "post"}-detail photo${files.length > 1 ? "s" : ""} posted`);
      setFiles([]); setCustomer(""); setNotes("");
      loadRecent();
    } catch (e) { toast.error(errFrom(e)); }
    finally { setLoading(false); }
  }

  return (
    <AppShell nav={EMPLOYEE_NAV} title="Customer Cars" subtitle="Pre / Post inspection">
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 brand-surface rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-lg bg-[#FF3B30]/15 text-[#FF3B30] flex items-center justify-center"><Car size={20} /></div>
            <div>
              <h2 className="font-heading text-2xl font-bold tracking-tight">Upload inspection photos</h2>
              <p className="text-zinc-500 text-sm">Multiple photos, auto timestamp + geo watermark for legal traceability.</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-overline">Customer name</Label>
              <Input value={customer} onChange={(e) => setCustomer(e.target.value)} className="mt-2 bg-[#0A0A0A] border-white/10 h-11" placeholder="e.g., John Doe" data-testid="customer-name-input" />
            </div>
            <div>
              <Label className="text-overline">Inspection type</Label>
              <Tabs value={type} onValueChange={setType} className="mt-2">
                <TabsList className="grid grid-cols-2 bg-[#0A0A0A] border border-white/10 h-11">
                  <TabsTrigger value="pre" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white" data-testid="type-pre">Pre-detail</TabsTrigger>
                  <TabsTrigger value="post" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white" data-testid="type-post">Post-detail</TabsTrigger>
                </TabsList>
                <TabsContent value="pre" />
                <TabsContent value="post" />
              </Tabs>
            </div>
          </div>

          <div className="mt-4">
            <Label className="text-overline">Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-2 bg-[#0A0A0A] border-white/10" rows={3} placeholder="Any visible damage, special instructions…" data-testid="customer-notes-input" />
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between">
              <Label className="text-overline">Photos · {files.length}/10</Label>
              {files.length > 0 && <button onClick={() => setFiles([])} className="text-xs text-zinc-500 hover:text-white" data-testid="clear-customer-photos-button">Clear all</button>}
            </div>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {files.map((f, idx) => (
                <div key={idx} className="relative rounded-lg overflow-hidden border border-white/10 group" data-testid={`customer-photo-thumb-${idx}`}>
                  <img src={f.preview} alt="" className="w-full h-32 object-cover" />
                  <button onClick={() => removeFile(idx)} className="absolute top-1 right-1 bg-black/70 hover:bg-red-500 rounded-full p-1 opacity-90" aria-label="Remove" data-testid={`remove-customer-photo-${idx}`}>
                    <X size={14} className="text-white" />
                  </button>
                </div>
              ))}
              {files.length < 10 && (
                <div className="grid grid-cols-1 gap-2">
                  <label className="brand-surface-2 rounded-lg p-3 cursor-pointer hover-lift flex flex-col items-center justify-center text-center min-h-[64px]" data-testid="customer-camera-capture">
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                    <Camera className="text-[#FF3B30]" size={18} />
                    <div className="text-[11px] mt-1 text-zinc-400">Capture</div>
                  </label>
                  <label className="brand-surface-2 rounded-lg p-3 cursor-pointer hover-lift flex flex-col items-center justify-center text-center min-h-[64px]" data-testid="customer-upload-image">
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                    <ImagePlus className="text-[#FF3B30]" size={18} />
                    <div className="text-[11px] mt-1 text-zinc-400">Add</div>
                  </label>
                </div>
              )}
            </div>
          </div>

          <Button onClick={submit} disabled={loading} className="mt-6 bg-[#FF3B30] hover:bg-[#E02920] h-12 px-8 w-full sm:w-auto" data-testid="submit-customer-inspection-button">
            {loading ? "Uploading…" : (<><Check size={18} className="mr-2" />Post {files.length > 0 ? `${files.length} photo${files.length > 1 ? "s" : ""}` : ""}</>)}
          </Button>
        </div>

        <div className="lg:col-span-2">
          <div className="brand-surface rounded-2xl p-6" data-testid="recent-inspections">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg">Your recent uploads</h3>
              <span className="text-overline">{recent.length}</span>
            </div>
            {recent.length === 0 ? (
              <div className="text-sm text-zinc-500 py-8 text-center">Nothing yet.</div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {recent.map((t) => (
                  <div key={t.id} className="rounded-lg overflow-hidden border border-white/10 bg-[#0A0A0A]">
                    <div className="relative">
                      <img src={absUrl(t.image_url || t.images?.[0]?.url)} alt="" className="w-full h-32 object-cover" />
                      <span className={`absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${t.inspection_type === "pre" ? "bg-amber-500/15 text-amber-400 border border-amber-500/30" : "bg-green-500/15 text-green-400 border border-green-500/30"}`}>{t.inspection_type}</span>
                      {(t.images?.length || 0) > 1 && (
                        <span className="absolute top-2 right-2 bg-black/75 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-white/10 text-white">+{t.images.length - 1}</span>
                      )}
                    </div>
                    <div className="p-2">
                      <div className="text-xs font-semibold truncate">{t.customer_name}</div>
                      <div className="text-[10px] text-zinc-500">{new Date(t.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
